/**
 * INT-02/04 — o link já lido vira o mesmo `state` que as telas do app mandam para Documentos.
 * Paciente por nome só entra com resultado único (RXD-04); a receita com remédio passa pelo
 * ditado, que busca o remédio no catálogo (RXD-05).
 */
import type { PrescriptionTemplate } from '@/entities/clinicalDocument/types'
import type { Patient } from '@/entities/patient/types'
import { resolvePatientByName } from '@/features/prescriptions/dictation/dictationToPrescription'
import type { PatientActionLocationState } from '@/shared/lib/patientNavigation'
import type { PrescriptionSignatureKind } from '@/shared/lib/digitalSignatureDocumentType'
import { prescriptionIntentPhrase, type ParsedIntent, type PrescriptionIntent } from './parseIntentParams'

const SIGN_KIND: Record<NonNullable<PrescriptionIntent['signKind']>, PrescriptionSignatureKind> = {
  simples: 'prescription_simple',
  controlada: 'prescription_controlled',
  antimicrobiano: 'prescription_antimicrobial',
}

const normalize = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()

/** Modelo de pedido de exame pelo nome: igual vence; senão, só se um único nome contiver o texto. */
export function findRequestTemplate(name: string, templates: ReadonlyArray<PrescriptionTemplate>): PrescriptionTemplate | null {
  const query = normalize(name)
  const requests = templates.filter((template) => template.type === 'request')
  const exact = requests.filter((template) => normalize(template.name) === query)
  if (exact.length === 1) return exact[0]!
  const partial = requests.filter((template) => normalize(template.name).includes(query))
  return partial.length === 1 ? partial[0]! : null
}

export function intentToNavigationState(
  intent: ParsedIntent,
  context: { patients: ReadonlyArray<Patient>; templates: ReadonlyArray<PrescriptionTemplate> },
): PatientActionLocationState {
  const match = intent.patient ? resolvePatientByName(intent.patient, context.patients) : null
  const patientId = match?.status === 'unique' ? match.candidates[0]!.id : undefined
  const base: PatientActionLocationState = { openDocumentosTab: 'new-document', ...(patientId ? { prefillPatientId: patientId } : {}) }

  if (intent.kind === 'receita') {
    const state: PatientActionLocationState = { ...base, prefillDocumentType: 'prescription' }
    if (intent.repeatLast && patientId) return { ...state, repeatLastPrescription: true }
    if (intent.signKind) state.prefillPrescriptionKind = SIGN_KIND[intent.signKind]
    if (intent.voice) state.startDictation = true
    if (intent.medication || intent.text) state.prefillDictation = prescriptionIntentPhrase(intent)
    return state
  }

  if (intent.kind === 'atestado') {
    const certificate = {
      ...(intent.days ? { days: intent.days } : {}),
      ...(intent.startDate ? { startDate: intent.startDate } : {}),
      ...(intent.cid ? { cidCode: intent.cid } : {}),
    }
    return {
      ...base,
      prefillDocumentType: 'certificate',
      ...(Object.keys(certificate).length ? { prefillCertificate: certificate } : {}),
    }
  }

  const template = intent.packageName ? findRequestTemplate(intent.packageName, context.templates) : null
  return {
    ...base,
    prefillDocumentType: 'request',
    ...(template ? { prefillTemplateId: template.id } : {}),
    ...(intent.text ? { prefillDocumentContent: intent.text } : {}),
  }
}
